// src/oura/components/docs/threads/fileApproved.tsx
//
// "File the approved PDF" (Docs phase 2, lane 4: 2.14), the doc drawer
// foot's primary action once every question is approved and this approval's
// PDF is not filed yet. A named person files it: the PDF of the approved doc
// goes to the client's Reports (report kind doc_approved), the doc stays as it
// is (no next version since #4874), and the client hears in the bell only. It
// asks first, naming who files it.

import { useState, type ReactNode } from "react";
import { FileCheck2Icon } from "lucide-react";
import { fileApprovedPdf, type ApprovedReport } from "@service/aeo/clientDocsWrite";
import type { StaffDocument } from "@service/aeo/clientDocs";
import { PillButton, type DrawerConfirm, type DrawerFootMessage } from "@oura/ui";
import { docsFailureText, type DocsLoad } from "@oura/docs";
import { useSignedInName } from "@oura/docs/signedIn";
import { filedForVersion } from "./model";

export interface FileApprovedAction {
    /** The foot's primary pill, or null when there is nothing to file (not approved, filed already, or the record not read yet). */
    button: ReactNode | null;
    confirm: DrawerConfirm | null;
}

/**
 * B24: a named person files the PDF of the approved doc to the client's
 * Reports; the doc stays as it is, and the client hears in the bell only. It asks first, naming who files it.
 * Call it on every render (it holds state); `doc` null offers nothing.
 */
export function useFileApproved({
    doc,
    businessName,
    reports,
    enabled,
    onBusy,
    onNote,
    onFiled,
}: {
    doc: StaffDocument | null;
    businessName: string;
    reports: DocsLoad<ApprovedReport[]>;
    /** The write switch and the file type allow it. */
    enabled: boolean;
    onBusy: (busy: boolean) => void;
    onNote: (m: DrawerFootMessage) => void;
    /** Filed: said in the foot, which then offers the next doc. */
    onFiled: (text: string) => void;
}): FileApprovedAction {
    const me = useSignedInName();
    // The doc id the question is open for, so it never carries to another doc.
    const [asking, setAsking] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    if (!doc || !enabled) return { button: null, confirm: null };

    const offer = doc.state === "approved" && reports.status === "ready" && !filedForVersion(reports.data, doc);
    const client = doc.sharing.sharedWith.find((p) => !p.ours && p.role !== "owner")?.name ?? "The client";
    const id = doc.id;

    const file = async () => {
        setBusy(true);
        onBusy(true);
        const r = await fileApprovedPdf(id);
        setBusy(false);
        onBusy(false);
        setAsking(null);
        if (!r.ok) return onNote({ tone: "error", text: docsFailureText(r, "the PDF") });
        onFiled(`Filed ${r.data.report.fileName} to the client's reports. A change from here comes back to the client for their yes. Open it under Approved`);
    };

    return {
        button: offer ? (
            <PillButton variant="primary" icon={FileCheck2Icon} writes disabled={busy} onClick={() => setAsking(id)}>
                File the approved PDF
            </PillButton>
        ) : null,
        confirm: asking === id
            ? {
                text: `File the PDF of the approved doc to ${businessName}'s reports${me ? ` as ${me}` : " under your name"}? The doc stays as it is. ${client} hears in the bell, with no email.`,
                verb: "File it",
                busy,
                onConfirm: () => void file(),
                onCancel: () => setAsking(null),
            }
            : null,
    };
}
